import { t as uiText } from '../i18n';
import React, { useState, useEffect } from 'react';
import { generateInviteLinkErrorMessage } from '../features/workspace/memberInviteMessages';
import { useParams } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { collection, getDocs, query, orderBy, doc, updateDoc } from 'firebase/firestore';
import { functions, db } from '../lib/firebase';

type Props = {
  onClose: () => void;
};

type IssuedLink = {
  id: string;
  url: string;
  expiryDays: string;
  requireApproval: boolean;
  createdAt: Date;
  isActive: boolean;
};

const InviteUrlModal: React.FC<Props> = ({ onClose }) => {
  const { workspaceId: routeWorkspaceId } = useParams();
  const [expiryDays, setExpiryDays] = useState<string>('7');
  const [requireApproval, setRequireApproval] = useState<boolean>(true);
  const [issuedLinks, setIssuedLinks] = useState<IssuedLink[]>([]);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [messageIsError, setMessageIsError] = useState(false);
  const [copyStatus, setCopyStatus] = useState<{ [key: string]: 'idle' | 'copying' | 'success' | 'error' }>({});
  const [deactivateStatus, setDeactivateStatus] = useState<{ [key: string]: 'idle' | 'deactivating' | 'success' | 'error' }>({});

  // 発行済みリンク一覧を取得
  const fetchIssuedLinks = async () => {
    if (!routeWorkspaceId) return;
    
    setLoading(true);
    try {
      const linksRef = collection(db, `workspaces/${routeWorkspaceId}/inviteLinks`);
      const q = query(linksRef, orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(q);
      
      const links: IssuedLink[] = snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: data.id,
          url: `${window.location.origin}/${routeWorkspaceId}/invite/${data.token}`,
          expiryDays: data.expiryDays,
          requireApproval: data.requireApproval,
          createdAt: data.createdAt?.toDate() || new Date(),
          isActive: data.isActive && (!data.expiresAt || data.expiresAt.toDate() > new Date()),
        };
      });
      
      setIssuedLinks(links);
    } catch (error) {
      console.error('発行済みリンクの取得に失敗:', error);
      setMessageIsError(true);
      setMessage(uiText("Failed to load the link list"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchIssuedLinks();
  }, [routeWorkspaceId]);

  // リンク発行処理
  const handleGenerateLink = async () => {
    if (!routeWorkspaceId || generating) return;
    
    setGenerating(true);
    setMessage(null);
    setMessageIsError(false);
    
    try {
      const generateInviteLink = httpsCallable(functions, 'generateInviteLinkFn');
      const result = await generateInviteLink({
        workspaceId: routeWorkspaceId,
        expiryDays: expiryDays,
        requireApproval: requireApproval,
      });
      
      const responseData = result.data as { 
        success: boolean; 
        linkId?: string; 
        token?: string; 
        url?: string; 
        error?: string; 
      };
      
      if (responseData.success) {
        setMessageIsError(false);
        setMessage(uiText("Invite link created"));
        // リンク一覧を再取得
        await fetchIssuedLinks();
      } else {
        setMessageIsError(true);
        setMessage(uiText("Failed to create the link"));
      }
    } catch (error: unknown) {
      console.error('リンク発行に失敗:', error);
      setMessageIsError(true);
      setMessage(generateInviteLinkErrorMessage(error));
    } finally {
      setGenerating(false);
    }
  };

  // クリップボードにコピーする関数
  const handleCopyToClipboard = async (url: string, linkId: string) => {
    setCopyStatus(prev => ({ ...prev, [linkId]: 'copying' }));
    
    try {
      // モダンブラウザ用のクリップボードAPI
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(url);
        setCopyStatus(prev => ({ ...prev, [linkId]: 'success' }));
        
        // 3秒後にステータスをリセット
        setTimeout(() => {
          setCopyStatus(prev => ({ ...prev, [linkId]: 'idle' }));
        }, 3000);
      } else {
        // フォールバック: 古いブラウザ用
        const textArea = document.createElement('textarea');
        textArea.value = url;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        
        try {
          document.execCommand('copy');
          setCopyStatus(prev => ({ ...prev, [linkId]: 'success' }));
          
          // 3秒後にステータスをリセット
          setTimeout(() => {
            setCopyStatus(prev => ({ ...prev, [linkId]: 'idle' }));
          }, 3000);
        } catch (err) {
          console.error('コピーに失敗しました:', err);
          setCopyStatus(prev => ({ ...prev, [linkId]: 'error' }));
          
          // 3秒後にステータスをリセット
          setTimeout(() => {
            setCopyStatus(prev => ({ ...prev, [linkId]: 'idle' }));
          }, 3000);
        } finally {
          document.body.removeChild(textArea);
        }
      }
    } catch (error) {
      console.error('クリップボードへのコピーに失敗しました:', error);
      setCopyStatus(prev => ({ ...prev, [linkId]: 'error' }));
      
      // 3秒後にステータスをリセット
      setTimeout(() => {
        setCopyStatus(prev => ({ ...prev, [linkId]: 'idle' }));
      }, 3000);
    }
  };

  // リンクを無効化する関数
  const handleDeactivateLink = async (linkId: string) => {
    if (!routeWorkspaceId) return;
    
    setDeactivateStatus(prev => ({ ...prev, [linkId]: 'deactivating' }));
    
    try {
      // Firestoreの招待リンクドキュメントを更新
      const inviteLinkRef = doc(db, `workspaces/${routeWorkspaceId}/inviteLinks/${linkId}`);
      await updateDoc(inviteLinkRef, {
        isActive: false,
        revokedAt: new Date()
      });
      
      setDeactivateStatus(prev => ({ ...prev, [linkId]: 'success' }));
      setMessageIsError(false);
      setMessage(uiText("Invite link revoked"));
      
      // リンク一覧を再取得
      await fetchIssuedLinks();
      
      // 3秒後にステータスをリセット
      setTimeout(() => {
        setDeactivateStatus(prev => ({ ...prev, [linkId]: 'idle' }));
      }, 3000);
      
    } catch (error) {
      console.error('リンクの無効化に失敗:', error);
      setDeactivateStatus(prev => ({ ...prev, [linkId]: 'error' }));
      setMessageIsError(true);
      setMessage(uiText("Failed to disable the link"));
      
      // 3秒後にステータスをリセット
      setTimeout(() => {
        setDeactivateStatus(prev => ({ ...prev, [linkId]: 'idle' }));
      }, 3000);
    }
  };
  return (
    <div
      style={{
        position: 'fixed',
        top: 0, left: 0, right: 0, bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.4)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 2000,
      }}
    >
      <div
        style={{
          backgroundColor: '#fff',
          borderRadius: '8px',
          width: '400px',
          padding: '20px',
          boxShadow: '0 4px 10px rgba(0,0,0,0.2)',
        }}
      >
        <div style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '16px', color: 'var(--workspace-primary, #cfae91)' }}>
          {uiText("Issue member invite link")}
        </div>

        <p style={{ fontSize: '12px', color: '#666', marginBottom: '16px' }}>
          {uiText("This URL is for requesting to join the team as a member")}<br />
          {uiText("You can set an expiration and require approval before someone can join")}
        </p>

        <div style={{ marginBottom: '16px' }}>
          <label style={{ display: 'block', fontSize: '14px', fontWeight: 'bold', marginBottom: '8px', color: '#333' }}>
            {uiText("Invite link expiration")}
          </label>
          <select
            value={expiryDays}
            onChange={(e) => setExpiryDays(e.target.value)}
            style={{
              width: '100%',
              padding: '8px',
              borderRadius: '4px',
              border: '1px solid #ccc',
              fontSize: '14px',
              backgroundColor: '#fff',
              outline: 'none',
              boxSizing: 'border-box',
            }}
            onFocus={(e) => {
              e.target.style.border = '2px solid var(--workspace-primary, #cfae91)';
            }}
            onBlur={(e) => {
              e.target.style.border = '1px solid #ccc';
            }}
          >
            <option value="1">{uiText("1 day")}</option>
            <option value="7">{uiText("7 days")}</option>
            <option value="30">{uiText("30 days")}</option>
            <option value="unlimited">{uiText("No expiry")}</option>
          </select>
        </div>

        <div style={{ marginBottom: '16px' }}>
          <label style={{ display: 'block', fontSize: '14px', fontWeight: 'bold', marginBottom: '8px', color: '#333' }}>
            {uiText("Whether joining requires approval")}
          </label>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <label style={{ display: 'flex', alignItems: 'center', fontSize: '14px', cursor: 'pointer' }}>
              <input
                type="radio"
                name="approval"
                checked={requireApproval}
                onChange={() => setRequireApproval(true)}
                style={{ 
                  marginRight: '8px',
                  accentColor: 'var(--workspace-primary, #cfae91)',
                  borderColor: '#fff'
                }}
              />
              <div>
                <div style={{ color: '#333', fontWeight: 'bold' }}>{uiText("With approval")}</div>
                <div style={{ fontSize: '12px', color: '#666', marginLeft: '20px' }}>{uiText("Join as a member after administrator approval")}</div>
              </div>
            </label>
            <label style={{ display: 'flex', alignItems: 'center', fontSize: '14px', cursor: 'pointer' }}>
              <input
                type="radio"
                name="approval"
                checked={!requireApproval}
                onChange={() => setRequireApproval(false)}
                style={{ 
                  marginRight: '8px',
                  accentColor: 'var(--workspace-primary, #cfae91)',
                  borderColor: '#fff'
                }}
              />
              <div>
                <div style={{ color: '#333', fontWeight: 'bold' }}>{uiText("Without approval")}</div>
                <div style={{ fontSize: '12px', color: '#666', marginLeft: '20px' }}>{uiText("Join as a member automatically by opening the link")}</div>
              </div>
            </label>
          </div>
        </div>

        <button
          onClick={handleGenerateLink}
          disabled={generating || !routeWorkspaceId}
          style={{
            width: '100%',
            padding: '10px',
            backgroundColor: generating || !routeWorkspaceId ? '#ccc' : 'var(--workspace-primary, #cfae91)',
            color: '#fff',
            border: 'none',
            borderRadius: '4px',
            fontWeight: 'bold',
            cursor: generating || !routeWorkspaceId ? 'not-allowed' : 'pointer',
            marginBottom: '8px',
          }}
        >
          {generating ? uiText("Issuing...") : uiText("Create link")}
        </button>

        {message && (
          <div style={{ 
            marginBottom: '16px', 
            fontSize: '14px', 
            color: messageIsError ? 'red' : 'green',
            textAlign: 'center',
          }}>
            {message}
          </div>
        )}

        {/* 発行済みリンク一覧 */}
        <div style={{ marginTop: '24px', marginBottom: '16px' }}>
          <h3 style={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '12px', color: '#333' }}>
            {uiText("Issued links")}
          </h3>
          
          {loading ? (
            <div style={{ padding: '16px', textAlign: 'center', color: '#666', fontSize: '14px' }}>
              {uiText("Loading now...")}
            </div>
          ) : issuedLinks.length === 0 ? (
            <div style={{ padding: '16px', textAlign: 'center', color: '#666', fontSize: '14px' }}>
              {uiText("No links issued")}
            </div>
          ) : (
            <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
              {issuedLinks.map((link) => (
                <div
                  key={link.id}
                  style={{
                    border: '1px solid #eee',
                    borderRadius: '4px',
                    padding: '12px',
                    marginBottom: '8px',
                    backgroundColor: link.isActive ? '#fff' : '#f8f9fa',
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                    <div style={{ fontSize: '12px', color: '#666' }}>
                      {uiText("Created on:")} {link.createdAt.toLocaleDateString('ja-JP')}
                    </div>
                    <div style={{
                      fontSize: '11px',
                      padding: '2px 6px',
                      borderRadius: '3px',
                      backgroundColor: link.isActive ? '#d4edda' : '#f8d7da',
                      color: link.isActive ? '#155724' : '#721c24',
                    }}>
                      {link.isActive ? uiText("Enabled") : uiText("Disabled")}
                    </div>
                  </div>
                  
                  <div style={{ fontSize: '12px', color: '#333', marginBottom: '4px', wordBreak: 'break-all' }}>
                    {link.url}
                  </div>
                  
                  <div style={{ display: 'flex', gap: '16px', fontSize: '11px', color: '#666' }}>
                    <span>
                      {uiText("Due:")} {link.expiryDays === 'unlimited' ? uiText("No expiry") : uiText("{{expiryDays}} days", { expiryDays: link.expiryDays })}
                    </span>
                    <span>
                      {uiText("Approval:")} {link.requireApproval ? uiText("Yes") : uiText("None")}
                    </span>
                  </div>
                  
                  {link.isActive && (
                    <div style={{ marginTop: '8px', display: 'flex', gap: '8px' }}>
                      <button
                        onClick={() => handleCopyToClipboard(link.url, link.id)}
                        disabled={copyStatus[link.id] === 'copying'}
                        style={{
                          fontSize: '11px',
                          padding: '4px 8px',
                          backgroundColor: copyStatus[link.id] === 'success' ? '#f5f2ed' : 
                                         copyStatus[link.id] === 'error' ? '#dc3545' : 'var(--workspace-primary, #cfae91)',
                          color: copyStatus[link.id] === 'success' ? '#8B7355' : '#fff',
                          border: 'none',
                          borderRadius: '3px',
                          cursor: copyStatus[link.id] === 'copying' ? 'not-allowed' : 'pointer',
                          transition: 'background-color 0.2s ease',
                          fontWeight: copyStatus[link.id] === 'success' ? 'bold' : 'normal',
                        }}
                      >
                        {copyStatus[link.id] === 'copying' ? uiText("Copying...") :
                         copyStatus[link.id] === 'success' ? uiText("Copied") :
                         copyStatus[link.id] === 'error' ? uiText("Copy failed") : uiText("Copy")}
                      </button>
                      <button
                        onClick={() => handleDeactivateLink(link.id)}
                        disabled={deactivateStatus[link.id] === 'deactivating'}
                        style={{
                          fontSize: '11px',
                          padding: '4px 8px',
                          backgroundColor: deactivateStatus[link.id] === 'success' ? '#28a745' : 
                                         deactivateStatus[link.id] === 'error' ? '#dc3545' : '#dc3545',
                          color: '#fff',
                          border: 'none',
                          borderRadius: '3px',
                          cursor: deactivateStatus[link.id] === 'deactivating' ? 'not-allowed' : 'pointer',
                          transition: 'background-color 0.2s ease',
                        }}
                      >
                        {deactivateStatus[link.id] === 'deactivating' ? uiText("Disabling...") :
                         deactivateStatus[link.id] === 'success' ? uiText("Disabled!") :
                         deactivateStatus[link.id] === 'error' ? uiText("Couldn't disable") : uiText("Disable")}
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        <button
          onClick={onClose}
          style={{
            marginTop: '16px',
            display: 'block',
            width: '100%',
            background: 'none',
            border: 'none',
            color: 'var(--workspace-primary, #cfae91)',
            cursor: 'pointer',
          }}
        >
          {uiText("Close")}
        </button>
      </div>
    </div>
  );
};

export default InviteUrlModal;
